import { useCallback, useEffect, useMemo, type ReactNode } from 'react'
import * as React from 'react'
import moment from 'moment'

import config from 'constants/config'
import { ChertoExperiments } from 'constants/experiments'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useExperiment from 'hooks/Experiments/useExperiment'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useAppSelector } from 'hooks/reduxHooks'
import { isInsideCancellationOptionCutoff } from 'lib/offer/cancellationOptionPricing'
import { buildFlashCancellationOptionRows } from 'lib/offer/flashCancellationOptions'
import { getBasePackageUniqueKey } from 'lib/offer/offerUtils'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getAllPackageViews } from 'selectors/offerPage/offerPageSelectors'

import FlashCancellationOptionSelector from './FlashCancellationOptionSelector'

const PURCHASE_CART_MODES: ReadonlySet<App.CheckoutCartMode> = new Set(['default', 'vertical-select'])

interface Props {
  packageView: App.OfferPackageOptionView
  offer: App.HotelOffer
  allPackageViews: Array<App.OfferPackageOptionView>
  checkIn?: string
  /** Rendered in place of the options when the package sells only one, so the policy line keeps its spot. */
  fallback?: ReactNode
}

export function FlashCancellationOptionPicker(props: Props) {
  const { packageView, offer, allPackageViews, checkIn, fallback } = props
  const offerPageDispatch = useOfferPageDispatch()
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const isPurchase = useAppSelector(state => PURCHASE_CART_MODES.has(state.checkout.cart.mode ?? 'default'))
  const { triggerActivation } = useExperiment(ChertoExperiments.flashCancellationOptions, { canActivate: false })

  const selectedKey = packageView.package.uniqueKey
  const baseKey = getBasePackageUniqueKey(packageView.package)
  const views = useMemo(
    () => allPackageViews.filter(view => getBasePackageUniqueKey(view.package) === baseKey),
    [allPackageViews, baseKey],
  )
  const rows = useMemo(() => buildFlashCancellationOptionRows(
    views,
    selectedKey,
    view => (canViewLuxPlusBenefits && view.memberPricing?.price) || view.pricing.price,
  ), [views, selectedKey, canViewLuxPlusBenefits])
  const reserveForZeroKeys = useMemo(() => {
    if (!config.RESERVE_FOR_ZERO_ENABLED && !isSpoofed) {
      return undefined
    }
    const checkInMoment = checkIn ? moment(checkIn) : undefined
    return new Set(rows
      .filter(row => isPackageEligibleForZeroReservation(offer, checkInMoment, row.policyType))
      .map(row => row.key))
  }, [rows, checkIn, isSpoofed, offer])

  const showsOptions = isPurchase && rows.length > 1
  const soldPolicies = new Set([
    packageView.package.roomRate?.cancellationPolicy?.type,
    ...(packageView.package.roomRate?.soldCancellationPolicies ?? []),
  ].filter(Boolean))
  const sellsOptions = isPurchase && soldPolicies.size > 1 && !isInsideCancellationOptionCutoff(checkIn, offer.property?.timezone)
  useEffect(() => {
    if (sellsOptions) {
      triggerActivation()
    }
  }, [sellsOptions, triggerActivation])

  const onChange = useCallback((key: string) => {
    offerPageDispatch({
      type: OfferPageActions.setCancellationOption,
      baseKey,
      policy: views.find(view => view.package.uniqueKey === key)?.package.cancellationOptionPolicy,
    })
  }, [baseKey, views, offerPageDispatch])

  if (!showsOptions) {
    return <>{fallback ?? null}</>
  }

  return <FlashCancellationOptionSelector
    rows={rows}
    reserveForZeroKeys={reserveForZeroKeys}
    checkIn={checkIn}
    propertyTimezoneOffset={offer.property?.timezoneOffset}
    numberOfDateChangesAllowed={offer.numberOfDateChangesAllowed}
    onChange={onChange}
  />
}

type MappedProps = Pick<Props, 'offer' | 'allPackageViews' | 'checkIn'>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  allPackageViews: getAllPackageViews(state, appState),
  checkIn: state.checkInDate,
}), FlashCancellationOptionPicker)
